import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BrowserProvider, ethers, Contract } from 'ethers';
import EthereumProvider, { EthereumProviderOptions } from '@walletconnect/ethereum-provider';
import { NgZone } from '@angular/core';
import ERC20_ABI from '../abis/ERC20.json'; // Minimal ERC-20 ABI
@Injectable({ providedIn: 'root' })


export class WalletService {
  private wc?: EthereumProvider;
  private browser?: BrowserProvider;
  private address: string | null = null;
  public chainId$ = new BehaviorSubject<number | null>(null);
  public chain$ = this.chainId$.asObservable();   // <-- à consommer côté composant

  private alchemyApiKey = 'gCvph7eowzBP0e2uUN177AELQnht-CXN'; 

  // Observable : adresse courante
  public address$ = new BehaviorSubject<string | null>(null);
  // Observable : URI WalletConnect (null = modale fermée)
  public wcUri$ = new BehaviorSubject<string | null>(null);
  public wcLastUri: string | null = null;   // ✅ dernier wcUri connu (raw)
  private autoRestored = false;
  // ───────────────────────── PUBLIC API ─────────────────────────
  constructor(private zone: NgZone) {
    this.attachEthEvents();   // ← listener installé quoi qu’il arrive
    this.autoRestore();            // ← nouveau
  }

  /** Pousse un wcUri dans le flux + le mémorise (et persiste pour debug). */
  public pushWcUri(uri: string | null): void {
    const v = uri || null;
    this.wcLastUri = v;
    this.wcUri$.next(v);

    try {
      if (v) {
        localStorage.setItem('wc_last_uri', v);
      } else {
        localStorage.removeItem('wc_last_uri');
      }
    } catch {
      // pas grave si localStorage indispo
    }
  }

  /** ───────── FACTORISÉ ──────── */
  private attachEthEvents() {
    const eth: any = (window as any).ethereum;
    if (!eth?.on) return;

    // (optionnel) évite les doublons si on l’appelle plusieurs fois
    eth.removeListener?.('chainChanged', this.onChainChanged);

    eth.on('chainChanged', this.onChainChanged);
  }

  private onChainChanged = (id: string | number | bigint) => {
    const num = typeof id === 'string' ? parseInt(id as string, 16) : Number(id);
    /* Toujours dans le NgZone pour que le template se rafraîchisse */
    this.zone.run(() => this.updateChainId(num));
  };

  private async autoRestore() {
    if (this.autoRestored) return;  // ← garde-fou
    this.autoRestored = true;
    const saved = localStorage.getItem('wallet');
    const eth = (window as any).ethereum;
    if (!saved || !eth) return;

    const provider = new ethers.BrowserProvider(eth);
    const { chainId } = await provider.getNetwork();
    const num = Number(chainId);
    console.debug("fffff"+this.chainId$.value);
    ///* ⬇️ NE RIEN FAIRE si un autre event l’a déjà renseigné */
    if (!this.chainId$.value) {
      this.zone.run(() => this.updateChainId(num));
      localStorage.removeItem('wallet');
    }
  }


  /**
   * Connecte le wallet (MetaMask desktop ou WalletConnect v2 mobile).
   * Résout avec l'adresse connectée.
   */
  async connect(): Promise<string> {
    this.attachEthEvents(); 
    if (this.address) return this.address;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const eth: any = (window as any).ethereum;

    // ───── Desktop : extension MetaMask ─────
    if (eth && !isMobile) {
      this.browser  = new BrowserProvider(eth);
      const [addr]  = await this.browser.send('eth_requestAccounts', []);
      if (!addr) throw new Error('no account');
      this.address = addr;
      this.address$.next(addr);
      localStorage.setItem('wallet', addr);

      /** ⬇️ Ajoute ça : écoute changement de chaîne MetaMask */
      eth.on?.('chainChanged', (id: string | number | bigint) => {
        const num = typeof id === 'string' ? parseInt(id, 16) : Number(id);
        this.updateChainId(num);
      });

      /** ⬇️ Pousse la chaîne dès la connexion */
      const { chainId } = await this.browser.getNetwork();
      this.updateChainId(Number(chainId));
      return addr;
    }

    // ───── Mobile : WalletConnect v2 ─────
    // 1) Session persistée ?
    const cached = localStorage.getItem('wcSession');
    if (cached) {
      await this.attachProvider(JSON.parse(cached));
      return this.address!;
    }

    // 2) Nouvelle session via backend
    const { sessionId, uri } = await this.createBackendSession();
    if (!sessionId || !uri) throw new Error('backend session error');

    localStorage.setItem('wc_session_id', sessionId);
    this.pushWcUri(uri);              // ✅ capture + flux + persist

    // ── Poll jusqu'à signature ──
    this.poll(sessionId);

    return new Promise<string>((resolve, reject) => {
      const sub = this.address$.subscribe(addr => {
        if (addr) { sub.unsubscribe(); resolve(addr); }
      });
      setTimeout(() => reject(new Error('WC connect timeout')), 120000);
    });
  }

  /** Déconnecte et purge tout. */
  async disconnect() {
    await this.wc?.disconnect().catch(() => {});
    this.clear();
  }
  private pushChain(id: string | number | bigint) {
    const numericId = Number(id);
    this.chainId$.next(numericId);
  }
  private updateChainId(id: string | number) {
    this.chainId$.next(Number(id));
  }

  /** Fournit le BrowserProvider (lance une erreur s'il n'existe pas). */
  getProvider(): BrowserProvider {
    if (!this.browser) throw new Error('provider not ready');
    return this.browser;
  }

  getRawProvider(): any {
    if (this.wc) return this.wc;                          // 🔗 WalletConnect v2
    const eth = (window as any).ethereum;                 // 🔗 MetaMask desktop
    if (eth) return eth;

    throw new Error('Aucun provider EIP-1193 disponible');
  }
  // ──────────────────────── Helpers internes ────────────────────────

  private async attachProvider(session: any) {
    this.wc = await EthereumProvider.init(this.getOptions(session));
    this.registerEvents();
    await this.wc.enable();
    this.afterEnable();
  }

  private getOptions(session?: any): EthereumProviderOptions {
    return {
      projectId: 'dd98c317e3c7c4bdef126b1f4f423eba',
      showQrModal: false,
      session,
      chains: [1],
      optionalChains: [1],
      rpcMap: { 1: 'https://rpc.ankr.com/eth' },
      methods: [
        'eth_sendTransaction',
        'personal_sign',
        'eth_signTypedData',
        'eth_sign'
      ],
      events: ['accountsChanged', 'chainChanged']
    } as EthereumProviderOptions;
  }

  private registerEvents() {
    this.wc?.on('display_uri', (uri: string) => {
      const raw = uri || '';
      this.pushWcUri(raw);  // ✅ capture + flux + persist
    });

    this.wc?.on('disconnect', () => this.clear());

    this.wc?.on('accountsChanged', (accounts: string[]) => {
      const addr = accounts?.[0] ?? null;
      this.address = addr;
      this.address$.next(addr);
    });

    // ➜ événement réseau
    this.wc?.on('chainChanged', (id: string | number | bigint) =>
      this.updateChainId(Number(id)));          // cast → number
  }

  async switchChain(chainId: number) {
    const provider: any = (window as any).ethereum ?? this.wc;
    if (!provider?.request) throw new Error('Provider EIP-1193 non dispo');

    const hex = '0x' + chainId.toString(16);

    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hex }]
      });
      /* Rien d’autre : dès que MetaMask accepte ou que l’utilisateur confirme,
         l’event "chainChanged" sera émis → updateChainId() tournera. */
    } catch (err: any) {
      if (err.code === 4902) {
        /* Chaîne inconnue ⇒ on la déclare et on re-switch */
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [this.getAddChainParams(chainId)]
        });
      } else {
        throw err; // autre erreur
      }
    }
  }

  getAddChainParams(id: number) {
    switch (id) {
      case 56: // ✅ BNB Chain
        return {
          chainId: '0x38',
          chainName: 'BNB Smart Chain',
          nativeCurrency: {
            name: 'BNB',
            symbol: 'BNB',
            decimals: 18
          },
          rpcUrls: ['https://bsc-dataseed.binance.org/'],
          blockExplorerUrls: ['https://bscscan.com']
        };

    case 137:   // Polygon
      return {
        chainId: '0x89',
        chainName: 'Polygon',
        nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
        rpcUrls: ['https://polygon-rpc.com'],
        blockExplorerUrls: ['https://polygonscan.com']
      };
    case 42161:  // Arbitrum
      return {
        chainId: '0xa4b1',
        chainName: 'Arbitrum One',
        nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
        rpcUrls: ['https://arb1.arbitrum.io/rpc'],
        blockExplorerUrls: ['https://arbiscan.io']
      };
    /* …complète si besoin … */
    default:
      throw new Error('Chaîne non supportée');
  }
  }

  async getOwnedTokensRaw(address: string, chainId: number): Promise<string[]> {
    if (!address) return [];

    try {
      // ✅ Appel JSON-RPC Alchemy
      const resp = await fetch(`${this.getAlchemyBaseUrl(chainId)}/v2/${this.alchemyApiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'alchemy_getTokenBalances',
          params: [address, 'erc20']
        })
      });

      if (!resp.ok) throw new Error(`Alchemy error: ${resp.status}`);
      const data = await resp.json();
      if (!data?.result?.tokenBalances) return [];

      // ✅ Retourne uniquement les adresses avec solde > 0
      return data.result.tokenBalances
        .filter((t: any) => BigInt(t.tokenBalance || '0') > 0n)
        .map((t: any) => (t.contractAddress || '').toLowerCase());

    } catch (err) {
      console.error('❌ Erreur getOwnedTokensRaw via Alchemy:', err);
      return await this.getOwnedTokensRawFallback(address, chainId);
    }
  }

  private async getOwnedTokensRawFallback(address: string, chainId: number): Promise<string[]> {
    console.warn('[WalletService] Fallback → scan whitelist interne');

    const provider = new BrowserProvider(this.getRawProvider());
    const whitelist = this.getTokenWhitelist(chainId);
    const owned: string[] = [];

    for (const token of whitelist) {
      try {
        let balance: bigint;
        if (token.address === ethers.ZeroAddress) {
          balance = await provider.getBalance(address);
        } else {
          const contract = new ethers.Contract(token.address, ERC20_ABI, provider);
          balance = await contract['balanceOf'](address);
        }
        if (balance > 0n) {
          owned.push(token.address.toLowerCase());
        }
      } catch { }
    }
    return owned;
  }

  isConnected(): boolean {
    return !!this.address$.value;
  }


  private getTokenWhitelist(chainId: number): any[] {
    switch (chainId) {
      case 1: // Ethereum
        return [
          { address: ethers.ZeroAddress, symbol: 'ETH', decimals: 18, chainId },
          { address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', symbol: 'USDC', decimals: 6, chainId },
          { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', symbol: 'USDT', decimals: 6, chainId }
        ];
      case 137: // Polygon
        return [
          { address: ethers.ZeroAddress, symbol: 'MATIC', decimals: 18, chainId },
          { address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', symbol: 'USDC', decimals: 6, chainId }
        ];
        case 56: // ✅ BNB Chain
      return [
        { address: ethers.ZeroAddress, symbol: 'BNB', decimals: 18, chainId },
        { address: '0x55d398326f99059fF775485246999027B3197955', symbol: 'USDT', decimals: 18, chainId }, // BEP-20
        { address: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d', symbol: 'USDC', decimals: 18, chainId }, // bridged
        { address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', symbol: 'WBNB', decimals: 18, chainId },
      ];
       case 42161: // Arbitrum
      return [
        { address: ethers.ZeroAddress, symbol: 'ETH', decimals: 18, chainId },
        { address: '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8', symbol: 'USDC', decimals: 6, chainId }, // Arbitrum USDC.e
        { address: '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9', symbol: 'USDT', decimals: 6, chainId },
        { address: '0x82af49447d8a07e3bd95bd0d56f35241523fbab1', symbol: 'WETH', decimals: 18, chainId },
      ];
      default:
        return [];
    }
  }

  private getAlchemyBaseUrl(chainId: number): string {
    switch (chainId) {
      case 1: return 'https://eth-mainnet.g.alchemy.com';
      case 137: return 'https://polygon-mainnet.g.alchemy.com';
      case 56: return 'https://bnb-mainnet.g.alchemy.com';
      case 42161: return 'https://arb-mainnet.g.alchemy.com';
      default: throw new Error(`ChainId ${chainId} non supporté par Alchemy`);
    }
  }

  private shortenAddress(addr: string): string {
    return addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';
  }


  private async afterEnable() {
    this.browser = new BrowserProvider(this.wc!);

    // adresse principale
    this.address = this.wc!.accounts[0];
    this.address$.next(this.address);

    // persiste la session pour « reconnect » auto
    localStorage.setItem('wcSession', JSON.stringify(this.wc!.session));

    // ferme la modale QR
    this.wcUri$.next(null);

    // ➜ récupère le réseau
    const { chainId } = await this.browser.getNetwork();   // chainId = bigint | number
    this.updateChainId(Number(chainId));                   // cast → number

    /** 2️⃣  écoute les changements venant du wallet */
    this.wc!.on('chainChanged', id => this.updateChainId(Number(id)));
  }


  private clear() {
    this.address = null;
    this.address$.next(null);
    this.browser = undefined;
    this.wc      = undefined;
    localStorage.removeItem('wcSession');
    localStorage.removeItem('wallet');
    localStorage.removeItem('wc_session_id');
    this.pushWcUri(null);
  }

  private isMobile() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  // ──────────────────────── Backend helpers ────────────────────────

  private async createBackendSession(): Promise<{ sessionId: string; uri: string }> {
    const res = await fetch('https://walletconnect-api.vercel.app/api/session', { method: 'POST' });
    if (!res.ok) throw new Error('backend error');
    return res.json();
  }

  private poll(sessionId: string) {
    const int = setInterval(async () => {
      try {
        const res = await fetch(`https://walletconnect-api.vercel.app/api/session?sessionId=${sessionId}`);
        if (res.status === 202) return;                   // toujours en attente
        if (res.status === 404) { clearInterval(int); this.wcUri$.next(null); return; }

        if (res.status === 200) {
          clearInterval(int);
          const session = await res.json();
          await this.attachProvider(session);
        }
      } catch {/* ignore réseau ; prochaine itération */}
    }, 3000);
  }
}
